package com.iptvplayer.shared.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.iptvplayer.shared.data.remote.api.WatchApi
import com.iptvplayer.shared.data.remote.api.WatchChatOutMsg
import com.iptvplayer.shared.data.remote.api.WatchRoomContent
import com.iptvplayer.shared.data.remote.api.WatchSetStateMsg
import com.iptvplayer.shared.data.remote.api.WatchWsMessage
import com.iptvplayer.shared.domain.repository.AuthRepository
import io.ktor.client.*
import io.ktor.client.plugins.websocket.*
import io.ktor.websocket.*
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

/** One chat line shown in the party panel. */
data class WatchChatLine(
    val peerId: String,
    val name: String,
    val text: String,
    val isSelf: Boolean = false,
    val isSystem: Boolean = false,
)

/** A playback snapshot pushed by the server (authoritative timekeeper). */
data class WatchSyncEvent(val playing: Boolean, val positionMs: Long)

data class WatchPartyUiState(
    /** True while a room session exists (hosting or joined). */
    val active: Boolean = false,
    val isHost: Boolean = false,
    val roomId: String? = null,
    val shareLink: String? = null,
    val connected: Boolean = false,
    val viewers: Int = 0,
    val chat: List<WatchChatLine> = emptyList(),
    /** Chat lines received while the panel is closed (badge count). */
    val unread: Int = 0,
    val content: WatchRoomContent? = null,
    val guestToken: String? = null,
    val error: String? = null,
)

/**
 * Native Watch Together client: create/join rooms, text chat, presence, and
 * server-authoritative playback sync over one WebSocket.
 *
 * The server re-broadcasts an authoritative `sync` snapshot on every state
 * change and every 3 s tick; the player applies it when drift exceeds a
 * threshold, and forwards local play/pause/seek as `set_state` (last-write-wins
 * for everyone in the room — same protocol the web client speaks).
 *
 * Voice chat and the web's host→guest WebRTC video broadcast are browser-only
 * (they need a WebRTC stack); native rooms rely on the sync protocol instead.
 */
class WatchTogetherViewModel(
    private val watchApi: WatchApi,
    private val httpClient: HttpClient,
    private val authRepository: AuthRepository,
    private val json: Json,
) : ViewModel() {

    private val _state = MutableStateFlow(WatchPartyUiState())
    val state: StateFlow<WatchPartyUiState> = _state.asStateFlow()

    private val _syncEvents = MutableSharedFlow<WatchSyncEvent>(extraBufferCapacity = 16)
    /** Server playback snapshots — the player screen collects and applies these. */
    val syncEvents: SharedFlow<WatchSyncEvent> = _syncEvents.asSharedFlow()

    private var wsJob: Job = Job().apply { complete() }
    private var session: DefaultClientWebSocketSession? = null
    private var selfId: String? = null

    /** Create a room for [content] and connect to it as the host. */
    fun startParty(content: WatchRoomContent) {
        if (_state.value.active) return
        viewModelScope.launch {
            try {
                val roomId = watchApi.createRoom(content)
                _state.update {
                    WatchPartyUiState(
                        active = true,
                        isHost = true,
                        roomId = roomId,
                        shareLink = WatchApi.shareLink(roomId),
                        content = content,
                    )
                }
                connect(roomId)
            } catch (e: Exception) {
                _state.update { it.copy(error = "Couldn't start the watch party: ${e.message}") }
            }
        }
    }

    /** Join an existing room (from a pasted link/code or a navigation arg). */
    fun joinParty(roomId: String) {
        if (_state.value.active && _state.value.roomId == roomId) return
        viewModelScope.launch {
            try {
                val joined = watchApi.joinRoom(roomId)
                _state.update {
                    WatchPartyUiState(
                        active = true,
                        isHost = false,
                        roomId = roomId,
                        shareLink = WatchApi.shareLink(roomId),
                        content = joined.content,
                        guestToken = joined.guestToken,
                    )
                }
                // Apply the initial snapshot before the first WS sync arrives.
                _syncEvents.tryEmit(WatchSyncEvent(joined.playing, joined.positionMs))
                connect(roomId)
            } catch (e: Exception) {
                _state.update {
                    it.copy(error = "Couldn't join — the watch party may have ended.")
                }
            }
        }
    }

    private fun connect(roomId: String) {
        wsJob.cancel()
        wsJob = viewModelScope.launch {
            val name = authRepository.getCurrentUser()?.username ?: "Guest"
            val token = _state.value.guestToken ?: authRepository.getStoredToken()?.accessToken
            var attempt = 0
            while (isActive && _state.value.active && _state.value.roomId == roomId) {
                try {
                    httpClient.webSocket(WatchApi.wsUrl(roomId, token, name)) {
                        session = this
                        attempt = 0
                        _state.update { it.copy(connected = true, error = null) }
                        for (frame in incoming) {
                            if (frame !is Frame.Text) continue
                            handleMessage(frame.readText())
                        }
                    }
                } catch (_: Exception) {
                    // fall through to the retry delay below
                }
                session = null
                _state.update { it.copy(connected = false) }
                if (!isActive || !_state.value.active) break
                attempt++
                if (attempt > MAX_RECONNECTS) {
                    _state.update { it.copy(error = "Lost connection to the watch party.") }
                    break
                }
                delay(RECONNECT_DELAY_MS)
            }
        }
    }

    private fun handleMessage(text: String) {
        val msg = runCatching { json.decodeFromString<WatchWsMessage>(text) }.getOrNull() ?: return
        when (msg.type) {
            "welcome" -> selfId = msg.selfId
            "room_state", "sync" -> {
                val playing = msg.playing ?: return
                val positionMs = msg.positionMs ?: return
                msg.viewers?.let { v -> _state.update { it.copy(viewers = v) } }
                _syncEvents.tryEmit(WatchSyncEvent(playing, positionMs))
            }
            "presence" -> msg.viewers?.let { v -> _state.update { it.copy(viewers = v) } }
            "chat" -> {
                val line = WatchChatLine(
                    peerId = msg.id.orEmpty(),
                    name = msg.name ?: "Guest",
                    text = msg.text.orEmpty(),
                    isSelf = msg.id != null && msg.id == selfId,
                )
                _state.update {
                    it.copy(
                        chat = (it.chat + line).takeLast(MAX_CHAT),
                        // Own echoed messages shouldn't light up the badge.
                        unread = if (line.isSelf) it.unread else it.unread + 1
                    )
                }
            }
            "peer_join" -> systemLine("${msg.name ?: "Someone"} joined")
            "peer_leave" -> {} // presence already updates the count; keep chat quiet
        }
    }

    private fun systemLine(text: String) {
        val line = WatchChatLine(peerId = "", name = "", text = text, isSystem = true)
        _state.update { it.copy(chat = (it.chat + line).takeLast(MAX_CHAT)) }
    }

    /** Forward a local play/pause/seek so everyone follows (last-write-wins). */
    fun sendPlaybackState(playing: Boolean, positionMs: Long) {
        val s = session ?: return
        viewModelScope.launch {
            runCatching { s.send(Frame.Text(json.encodeToString(WatchSetStateMsg(playing, positionMs)))) }
        }
    }

    fun sendChat(text: String) {
        val trimmed = text.trim().take(2000)
        if (trimmed.isEmpty()) return
        val s = session ?: return
        viewModelScope.launch {
            runCatching { s.send(Frame.Text(json.encodeToString(WatchChatOutMsg(trimmed)))) }
        }
    }

    /** Clear the unread badge (call when the chat panel opens). */
    fun markChatRead() {
        _state.update { it.copy(unread = 0) }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }

    /** Leave the room and drop the connection. */
    fun leave() {
        wsJob.cancel()
        val s = session
        session = null
        selfId = null
        if (s != null) {
            viewModelScope.launch { runCatching { s.close(CloseReason(CloseReason.Codes.NORMAL, "left")) } }
        }
        _state.value = WatchPartyUiState()
    }

    override fun onCleared() {
        leave()
        super.onCleared()
    }

    private companion object {
        const val MAX_CHAT = 200
        const val MAX_RECONNECTS = 5
        const val RECONNECT_DELAY_MS = 3_000L
    }
}
